// PWA registration and utilities
import { registerSW } from 'virtual:pwa-register';
import axios from 'axios';
import api from '@/services/axios'
// Define constants for PWA
export const NOTIFICATION_PERMISSION = {
  GRANTED: 'granted',
  DENIED: 'denied',
  DEFAULT: 'default'
};

// We're using Vite's proxy, so we can use relative URLs
export const API_BASE_URL = '';

// Register service worker with auto-update and safety timeouts
const updateSW = registerSW({
  // Set a short timeout for the refresh prompt to prevent hanging
  immediate: true,
  onNeedRefresh() {
    localStorage.setItem('updateAvailable', 'true');
    window.dispatchEvent(new Event('show-update-banner'));

    // Automatically apply the update without user confirmation
    // updateSW.updateServiceWorker(true);
  },
  onOfflineReady() {
  },
  onRegistered(r: any) {

    // Enable navigation preload if supported
    if (r && r.navigationPreload) {
      r.navigationPreload.enable().catch(error => {
      });
    }

    // Check for updates periodically
    setInterval(() => {
      r.update().catch((error) => {})
    }, 86400000) // Check every day
  },
  onRegisterError(error: any) {
    // Continue app initialization even if service worker fails
  }
});

// Periodic sync functionality has been removed
// We no longer need background sync as we're not supporting offline functionality

// Function to request notification permission
export async function requestNotificationPermission(): Promise<string> {
  if (!('Notification' in window)) {
    return NOTIFICATION_PERMISSION.DENIED;
  }

  if (Notification.permission === NOTIFICATION_PERMISSION.GRANTED) {
    return NOTIFICATION_PERMISSION.GRANTED;
  }

  if (Notification.permission !== NOTIFICATION_PERMISSION.DENIED) {
    const permission = await Notification.requestPermission();
    return permission;
  }

  return Notification.permission;
}

// Function to send a test notification
export function sendTestNotification() {
  if (Notification.permission === NOTIFICATION_PERMISSION.GRANTED) {
    const notification = new Notification('BoxBuddy', {
      body: 'This is a test notification',
      icon: '/pwa/pwa-192x192.png',
      badge: '/pwa/pwa-96x96.png',
      // vibrate: [100, 50, 100],
      silent: false,
    });

    // Play notification sound
    const audio = new Audio('/notification.wav');
    audio.play().catch(error => console.error('Error playing notification sound:', error));

    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  }
}

// Pull-to-refresh functionality completely removed
// Empty function for compatibility with existing code
export function setupPullToRefresh(_container: HTMLElement, _onRefresh: () => Promise<void>) {
  // Return empty cleanup function for compatibility
  return {
    cleanup: () => {}
  };
}

// Function to register the push notification service worker
export async function registerPushNotificationServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  try {
    if ('serviceWorker' in navigator) {
      // Wait for the service worker to be ready
      await navigator.serviceWorker.ready;

      // Check if we already have a push notification service worker
      const existingRegistration = await navigator.serviceWorker.getRegistration('/push-notification-sw.js');

      if (existingRegistration) {
        return existingRegistration;
      }

      // Register the push notification service worker
      const registration = await navigator.serviceWorker.register('/push-notification-sw.js', {
        scope: '/'
      });


      return registration;
    }
    return null;
  } catch (error) {
    console.error('Push Notification Service Worker registration failed:', error);
    return null;
  }
}

// Function to register for push notifications
export async function registerForPushNotifications(): Promise<boolean> {
  try {
    // Request notification permission first
    const permission = await requestNotificationPermission();
    if (permission !== NOTIFICATION_PERMISSION.GRANTED) {
      console.warn('Notification permission not granted');
      return false;
    }

    // Register the service worker
    const registration = await registerPushNotificationServiceWorker();
    if (!registration) {
      console.error('Failed to register push notification service worker');
      return false;
    }

    // Get VAPID public key from server
    // Add debugging to see what's happening with the request
    let data;
    try {
      // Get CSRF token from meta tag
      // const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

      // Use the correct API endpoint path without the /api prefix
      const response = await api.get(`${import.meta.env.VITE_API_URL}/api/webpush/vapid-public-key`);

      // Log the raw response for debugging
      // Check if response is OK
      data = response.data;
    } catch (fetchError) {
      return false;
    }

    if (!data.vapidPublicKey) {
      return false;
    }


    // Convert the key to the format required by the browser
    const convertedVapidKey = urlBase64ToUint8Array(data.vapidPublicKey);

    // Get push subscription
    let subscription = await registration.pushManager.getSubscription();

    // If subscription exists, check if it's using the same key
    if (subscription) {
      const options = subscription.options;
      const currentKey = options?.applicationServerKey;

      // Compare keys if available
      if (currentKey) {
        const currentKeyBase64 = btoa(String.fromCharCode.apply(null, new Uint8Array(currentKey)));
        const newKeyBase64 = btoa(String.fromCharCode.apply(null, convertedVapidKey));

        // If keys match, no need to resubscribe
        if (currentKeyBase64 === newKeyBase64) {
          await sendSubscriptionToServer(subscription);
          return true;
        }
      }

      // Keys don't match or couldn't compare, unsubscribe
      await subscription.unsubscribe();
    }

    // Create a new subscription
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: convertedVapidKey
    });


    // Send subscription to server
    await sendSubscriptionToServer(subscription);

    return true;
  } catch (error) {
    console.error('Error registering for push notifications:', error);
    return false;
  }
}

// Helper function to convert base64 to Uint8Array for VAPID key
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

// Function to send subscription to server
export async function sendSubscriptionToServer(subscription: PushSubscription): Promise<void> {
  try {
    if (!subscription || !subscription.endpoint) {
      console.error('Invalid subscription object');
      return;
    }

    // Get CSRF token from meta tag


    // Convert subscription to JSON to ensure we have the right format
    const subscriptionJSON = subscription.toJSON();

    // Log subscription details for debugging (excluding sensitive info)

    // Use the correct endpoint path without the /api prefix
    const response = await api.post(`${import.meta.env.VITE_API_URL}/webpush/subscribe`, subscriptionJSON);

  } catch (error) {
    throw error; // Re-throw to allow caller to handle
  }
}

// Function to setup splash screen with safety timeout
export function setupSplashScreen(): void {
  // Create splash screen element
  const splash = document.createElement('div');
  splash.id = 'app-splash-screen';
  splash.style.position = 'fixed';
  splash.style.top = '0';
  splash.style.left = '0';
  splash.style.width = '100%';
  splash.style.height = '100%';
  splash.style.backgroundColor = '#ffffff';
  splash.style.display = 'flex';
  splash.style.flexDirection = 'column';
  splash.style.justifyContent = 'center';
  splash.style.alignItems = 'center';
  splash.style.zIndex = '9999';
  splash.style.transition = 'opacity 0.5s ease-out';

  // Add logo
  const logo = document.createElement('img');
  logo.src = '/logo.svg';
  logo.alt = 'BoxBuddy';
  logo.style.width = '150px';
  logo.style.marginBottom = '20px';

  // Add loading spinner
  const spinner = document.createElement('div');
  spinner.className = 'spinner';
  spinner.innerHTML = '<svg class="w-10 h-10 text-green-600 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>';

  // Add elements to splash screen
  splash.appendChild(logo);
  splash.appendChild(spinner);

  // Add splash screen to body
  document.body.appendChild(splash);

  // Add a safety timeout to ensure splash screen is always removed
  // even if the load event doesn't fire properly
  const safetyTimeout = setTimeout(() => {
    if (splash.parentNode) {
      splash.style.opacity = '0';
      setTimeout(() => {
        if (splash.parentNode) {
          splash.parentNode.removeChild(splash);
        }
      }, 500);
    }
  }, 5000); // 5 seconds maximum display time

  // Hide splash screen when app is ready
  window.addEventListener('load', () => {
    // Give a little extra time for the app to render
    setTimeout(() => {
      splash.style.opacity = '0';

      // Remove splash screen after transition
      setTimeout(() => {
        if (splash.parentNode) {
          splash.parentNode.removeChild(splash);
        }
        clearTimeout(safetyTimeout);
      }, 500);
    }, 1000);
  });
}

// Offline functionality has been removed
// We no longer use IndexedDB for offline data storage
// This ensures users can only access the app when online

// Function to check if app is online
export function isOnline(): boolean {
  return navigator.onLine;
}

// Function to register online/offline event listeners
export function registerConnectivityListeners(onOnline: () => void, onOffline: () => void): () => void {
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);

  // Return cleanup function
  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
}

// Initialize PWA features with safety checks
export function initializePWA(): void {
  // Track initialization to prevent duplicate calls
  if ((window as any).__pwaInitialized) {
    return;
  }
  (window as any).__pwaInitialized = true;

  // Setup splash screen only in standalone mode
  if (window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator?.standalone === true) {
    try {
      setupSplashScreen();
    } catch (error) {
      // Continue initialization even if splash screen fails
    }
  }
}

// Initialize PWA features
initializePWA();

// Export the update function
export { updateSW };

// Function to debug service worker and push notification status
export async function debugServiceWorkerStatus(): Promise<void> {

  // Check if service workers are supported
  if (!('serviceWorker' in navigator)) {
    console.error('Service workers are not supported in this browser');
    return;
  }

  // Check service worker registration
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
    } else {
      console.warn('⚠️ No service worker registration found');
    }
  } catch (error) {
    console.error('❌ Error getting service worker registration:', error);
  }

  // Check push manager support
  if (!('PushManager' in window)) {
    console.error('❌ Push notifications are not supported in this browser');
    return;
  }

  // Check notification permission

  // Check push subscription
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
    } else {
      console.warn('⚠️ No push subscription found');
    }
  } catch (error) {
    console.error('❌ Error checking push subscription:', error);
  }

}

// Function to force clear all caches and reload
export async function forceClearCacheAndReload(): Promise<void> {

  try {
    // Clear all caches
    const cacheNames = await caches.keys();
    await Promise.all(
      cacheNames.map(cacheName => {
        return caches.delete(cacheName);
      })
    );


    // Unregister service worker
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      await registration.unregister();
    }

    // Reload the page
    window.location.reload();

  } catch (error) {
  }
}
